import type { QueryClient, QueryFilters } from "@tanstack/react-query";
import { isApiError } from "@bw/shared/api";
import type { ConversationResponse } from "@bw/shared/types/generated";

import { queryKeys } from "../query/queryClient";

type AuthorityVersions = { all: number; conversations: Map<string, number> };
const authorityVersions = new WeakMap<QueryClient, AuthorityVersions>();

export async function refreshInquiryAuthority(
  client: QueryClient,
  conversationId: string,
): Promise<void> {
  const filters: QueryFilters = {
    queryKey: ["social", "inquiry"],
    predicate: (query) => query.queryKey[3] === conversationId,
  };
  await client.cancelQueries(filters);
  await client.invalidateQueries(filters);
}

export async function refreshConversationAuthorityAfterMutationError(
  client: QueryClient,
  conversationId: string,
  communityId: string | undefined,
  error: unknown,
): Promise<void> {
  // A write denial may mean archival, not loss of readable history. Ask the
  // canonical owner instead of inferring revocation from a status code.
  if (
    !isApiError(error) ||
    ![400, 401, 403, 404, 409, 429].includes(error.status)
  )
    return;
  if (communityId && error.status !== 429) {
    const queryKey = queryKeys.social.communityChannels(communityId);
    // Mounted channel readers append the actor UID. Match their prefix without
    // copying authority between actor caches; only active readers refetch.
    await client.cancelQueries({ queryKey });
    await client.invalidateQueries({ queryKey });
  }
  await refreshInquiryAuthority(client, conversationId);
}

function versionsFor(client: QueryClient): AuthorityVersions {
  const existing = authorityVersions.get(client);
  if (existing) return existing;
  const versions: AuthorityVersions = { all: 0, conversations: new Map() };
  authorityVersions.set(client, versions);
  // Cache clear/GC must not let an old completion recreate removed history.
  // This listener has the same lifetime as its QueryClient, not a mounted view.
  client.getQueryCache().subscribe((event) => {
    const key = event.query.queryKey;
    if (
      event.type === "removed" &&
      key[0] === queryKeys.social.allMessages[0] &&
      typeof key[1] === "string"
    ) {
      versions.conversations.set(
        key[1],
        (versions.conversations.get(key[1]) ?? 0) + 1,
      );
    }
  });
  return versions;
}

export function conversationAuthorityVersion(
  client: QueryClient,
  conversationId: string,
): string {
  const versions = versionsFor(client);
  return `${versions.all}:${versions.conversations.get(conversationId) ?? 0}`;
}

async function filterConversationCache(
  queryClient: QueryClient,
  keep: (conversation: ConversationResponse) => boolean,
): Promise<void> {
  const queryKey = queryKeys.social.conversations();
  // Stop an older list response before installing the server-confirmed
  // revocation. A failed follow-up refetch may keep the safe remainder, but it
  // must never restore identity or preview data that the viewer can no longer
  // access.
  await queryClient.cancelQueries({ queryKey, exact: true });
  queryClient.setQueryData<ConversationResponse[]>(queryKey, (current) =>
    current?.filter(keep),
  );
}

/** Remove every cached inbox row involving a newly blocked player. */
export async function revokeBlockedPlayerConversations(
  queryClient: QueryClient,
  userId: string,
): Promise<void> {
  versionsFor(queryClient).all += 1;
  await queryClient.cancelQueries({ queryKey: queryKeys.social.allMessages });
  queryClient.removeQueries({ queryKey: queryKeys.social.allMessages });
  await filterConversationCache(queryClient, (conversation) =>
    conversation.participants.every(
      (participant) => participant.user_id !== userId,
    ),
  );
}

/** Remove one inbox row after the server revokes this conversation authority. */
export async function revokeConversation(
  queryClient: QueryClient,
  conversationId: string,
): Promise<void> {
  const versions = versionsFor(queryClient);
  versions.conversations.set(
    conversationId,
    (versions.conversations.get(conversationId) ?? 0) + 1,
  );
  const messageQueryKey = queryKeys.social.conversationMessages(conversationId);
  await queryClient.cancelQueries({ queryKey: messageQueryKey });
  queryClient.removeQueries({ queryKey: messageQueryKey });
  await filterConversationCache(
    queryClient,
    (conversation) => conversation.conversation_id !== conversationId,
  );
}
